import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { C } from '../tokens';
import Icon from '../components/Icon';
import { Card, Pill, Btn, Breadcrumb } from '../components/UI';

/* -----------------------------------------------------------
   Constants
   ----------------------------------------------------------- */
const STAGES = ['Qualified', 'New', 'Engaged', 'Nurturing', 'Closed'];
const INTERESTS = ['AI Integration', 'SaaS Automation', 'Data Analytics', 'Machine Learning', 'Cloud Systems', 'API Design', 'SaaS Flow', 'NLP Solutions', 'DevOps', 'Cybersecurity'];
const STAGE_COLORS = { Qualified: C.primary, New: '#3b82f6', Engaged: C.green, Nurturing: C.amber, Closed: C.t3 };
const PAGE_SIZE = 8;

const ACTIVE_THRESHOLDS = ['Just now', '10 mins ago', '30 mins ago', '1 hour ago', '2 hours ago', '3 hours ago', '4 hours ago', '5 hours ago', '12 hours ago'];
const isActive = (t) => ACTIVE_THRESHOLDS.includes(t);

/* -----------------------------------------------------------
   42 Seed Contacts
   ----------------------------------------------------------- */
const SEED = [
  { name: 'Aditya Wijaya',      email: 'aditya.w@gmail.com',       phone: '+62 812-3456-7890', interest: 'AI Integration',   convos: 12, lastActive: '2 hours ago',  stage: 'Qualified'  },
  { name: 'Siti Rahayu',        email: 'siti.rahayu@outlook.id',   phone: '+62 811-9876-5432', interest: 'SaaS Automation',  convos: 8,  lastActive: '5 hours ago',  stage: 'New'        },
  { name: 'Budi Santoso',       email: 'budi.s@tech.id',           phone: '+62 813-2233-4455', interest: 'Data Analytics',   convos: 24, lastActive: '1 day ago',    stage: 'Engaged'    },
  { name: 'Putri Pratama',      email: 'putri.p@corp.id',          phone: '+62 856-7788-9900', interest: 'Machine Learning', convos: 5,  lastActive: '3 days ago',   stage: 'Nurturing'  },
  { name: 'Dian Saputra',       email: 'dian.sap@startup.com',     phone: '+62 819-0011-2233', interest: 'Cloud Systems',    convos: 15, lastActive: '4 hours ago',  stage: 'Qualified'  },
  { name: 'Maya Indah',         email: 'maya.indah@service.id',    phone: '+62 821-4455-6677', interest: 'API Design',       convos: 3,  lastActive: '1 week ago',   stage: 'New'        },
  { name: 'Rizky Fauzi',        email: 'rizky.f@dev.io',           phone: '+62 878-8899-0011', interest: 'SaaS Flow',        convos: 19, lastActive: '10 mins ago',  stage: 'Engaged'    },
  { name: 'Eko Susanto',        email: 'eko.s@enterprise.com',     phone: '+62 812-5566-7788', interest: 'Data Analytics',   convos: 2,  lastActive: '2 weeks ago',  stage: 'Closed'     },
  { name: 'Rina Kusuma',        email: 'rina.k@fintech.id',        phone: '+62 815-1122-3344', interest: 'NLP Solutions',    convos: 7,  lastActive: '1 hour ago',   stage: 'Qualified'  },
  { name: 'Arief Rahman',       email: 'arief.r@media.co',         phone: '+62 817-5566-7788', interest: 'DevOps',           convos: 14, lastActive: '30 mins ago',  stage: 'Engaged'    },
  { name: 'Dewi Lestari',       email: 'dewi.l@agency.id',         phone: '+62 818-9900-1122', interest: 'Cybersecurity',    convos: 6,  lastActive: '3 days ago',   stage: 'Nurturing'  },
  { name: 'Hendra Gunawan',     email: 'hendra.g@corp.io',         phone: '+62 813-3344-5566', interest: 'AI Integration',   convos: 31, lastActive: '12 hours ago', stage: 'Qualified'  },
  { name: 'Lina Marlina',       email: 'lina.m@shop.id',           phone: '+62 819-7788-9900', interest: 'SaaS Automation',  convos: 4,  lastActive: '5 days ago',   stage: 'New'        },
  { name: 'Fajar Nugroho',      email: 'fajar.n@tech.co',          phone: '',                  interest: 'Machine Learning', convos: 22, lastActive: '2 hours ago',  stage: 'Engaged'    },
  { name: 'Aisyah Putri',       email: 'aisyah.p@uni.ac.id',       phone: '+62 856-1122-3344', interest: 'Data Analytics',   convos: 9,  lastActive: '1 day ago',    stage: 'Nurturing'  },
  { name: 'Yusuf Hakim',        email: 'yusuf.h@bank.id',          phone: '+62 811-5566-7788', interest: 'Cloud Systems',    convos: 18, lastActive: '4 hours ago',  stage: 'Qualified'  },
  { name: 'Nadia Safitri',      email: '',                         phone: '+62 878-3344-5566', interest: 'API Design',       convos: 1,  lastActive: '3 weeks ago',  stage: 'Closed'     },
  { name: 'Bayu Pratama',       email: 'bayu.p@logistics.id',      phone: '+62 812-9900-1122', interest: 'DevOps',           convos: 11, lastActive: '5 hours ago',  stage: 'Engaged'    },
  { name: 'Citra Dewi',         email: 'citra.d@health.co',        phone: '+62 815-3344-5566', interest: 'NLP Solutions',    convos: 8,  lastActive: '2 days ago',   stage: 'New'        },
  { name: 'Gilang Permana',     email: 'gilang.p@auto.id',         phone: '+62 817-7788-9900', interest: 'SaaS Flow',        convos: 16, lastActive: '1 hour ago',   stage: 'Qualified'  },
  { name: 'Tari Handayani',     email: 'tari.h@design.co',         phone: '+62 818-1122-3344', interest: 'Cybersecurity',    convos: 5,  lastActive: '1 week ago',   stage: 'Nurturing'  },
  { name: 'Oscar Wijaya',       email: 'oscar.w@game.io',          phone: '',                  interest: 'AI Integration',   convos: 27, lastActive: '30 mins ago',  stage: 'Engaged'    },
  { name: 'Fitri Amalia',       email: 'fitri.a@edu.id',           phone: '+62 856-5566-7788', interest: 'Machine Learning', convos: 3,  lastActive: '2 weeks ago',  stage: 'Closed'     },
  { name: 'Rendi Saputra',      email: 'rendi.s@mining.co',        phone: '+62 811-9900-1122', interest: 'Cloud Systems',    convos: 20, lastActive: '3 hours ago',  stage: 'Qualified'  },
  { name: 'Wulan Sari',         email: 'wulan.s@travel.id',        phone: '+62 878-1122-3344', interest: 'SaaS Automation',  convos: 7,  lastActive: '5 days ago',   stage: 'New'        },
  { name: 'Andi Kurniawan',     email: 'andi.k@retail.id',         phone: '+62 812-3344-5566', interest: 'Data Analytics',   convos: 13, lastActive: '12 hours ago', stage: 'Engaged'    },
  { name: 'Sinta Dewi',         email: 'sinta.d@fashion.co',       phone: '+62 815-7788-9900', interest: 'API Design',       convos: 2,  lastActive: '1 month ago',  stage: 'Closed'     },
  { name: 'Dimas Prasetyo',     email: 'dimas.p@energy.id',        phone: '+62 817-1122-3344', interest: 'DevOps',           convos: 17, lastActive: '4 hours ago',  stage: 'Qualified'  },
  { name: 'Laras Puspita',      email: 'laras.p@pharma.co',        phone: '+62 818-5566-7788', interest: 'NLP Solutions',    convos: 6,  lastActive: '2 days ago',   stage: 'Nurturing'  },
  { name: 'Irfan Maulana',      email: 'irfan.m@construct.id',     phone: '+62 813-9900-1122', interest: 'Cybersecurity',    convos: 10, lastActive: '1 day ago',    stage: 'Engaged'    },
  { name: 'Kirana Ayu',         email: 'kirana.a@beauty.id',       phone: '+62 819-3344-5566', interest: 'SaaS Flow',        convos: 4,  lastActive: '3 days ago',   stage: 'New'        },
  { name: 'Rama Putra',         email: 'rama.p@finserv.co',        phone: '+62 856-9900-1122', interest: 'AI Integration',   convos: 25, lastActive: '1 hour ago',   stage: 'Qualified'  },
  { name: 'Tiara Anggraini',    email: 'tiara.a@media.id',         phone: '',                  interest: 'Machine Learning', convos: 8,  lastActive: '5 hours ago',  stage: 'Nurturing'  },
  { name: 'Vino Bastian',       email: 'vino.b@sport.co',          phone: '+62 878-5566-7788', interest: 'Cloud Systems',    convos: 11, lastActive: '2 hours ago',  stage: 'Engaged'    },
  { name: 'Zahra Mustika',      email: 'zahra.m@law.id',           phone: '+62 812-7788-9900', interest: 'Data Analytics',   convos: 3,  lastActive: '1 week ago',   stage: 'Closed'     },
  { name: 'Angga Saputra',      email: 'angga.s@property.id',      phone: '+62 815-1122-9900', interest: 'SaaS Automation',  convos: 15, lastActive: '10 mins ago',  stage: 'Qualified'  },
  { name: 'Bella Safira',       email: 'bella.s@creative.co',      phone: '+62 817-3344-1122', interest: 'API Design',       convos: 6,  lastActive: '4 hours ago',  stage: 'Engaged'    },
  { name: 'Cahya Pratama',      email: '',                         phone: '+62 818-7788-3344', interest: 'DevOps',           convos: 1,  lastActive: '3 weeks ago',  stage: 'New'        },
  { name: 'Dinda Permata',      email: 'dinda.p@telco.id',         phone: '+62 813-5566-1122', interest: 'NLP Solutions',    convos: 9,  lastActive: '1 day ago',    stage: 'Nurturing'  },
  { name: 'Ega Firmansyah',     email: 'ega.f@insurance.co',       phone: '+62 819-9900-5566', interest: 'Cybersecurity',    convos: 14, lastActive: '2 hours ago',  stage: 'Qualified'  },
  { name: 'Fania Rahmawati',    email: 'fania.r@hotel.id',         phone: '+62 856-3344-7788', interest: 'SaaS Flow',        convos: 7,  lastActive: '5 days ago',   stage: 'Engaged'    },
  { name: 'Galih Wicaksono',    email: 'galih.w@transport.id',     phone: '+62 811-7788-5566', interest: 'AI Integration',   convos: 21, lastActive: '30 mins ago',  stage: 'Qualified'  },
].map((c, i) => ({ ...c, id: i + 1 }));

/* -----------------------------------------------------------
   Shared modal backdrop
   ----------------------------------------------------------- */
function Backdrop({ children, onClose }) {
  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, zIndex: 100,
      background: 'rgba(34,22,16,.32)', backdropFilter: 'blur(2px)',
      display: 'grid', placeItems: 'center', padding: 16,
      animation: 'fadeIn .15s ease',
    }}>
      <style>{`@keyframes fadeIn{from{opacity:0}to{opacity:1}} @keyframes slideUp{from{opacity:0;transform:translateY(16px)}to{opacity:1;transform:translateY(0)}}`}</style>
      <div onClick={e => e.stopPropagation()} style={{ animation: 'slideUp .2s ease', width: '100%', display: 'flex', justifyContent: 'center' }}>
        {children}
      </div>
    </div>
  );
}

/* -----------------------------------------------------------
   Delete confirmation popover
   ----------------------------------------------------------- */
function DeleteConfirm({ name, onConfirm, onCancel }) {
  return (
    <div style={{
      position: 'absolute', right: 0, top: '100%', zIndex: 20, marginTop: 4,
      width: 240, background: C.w, borderRadius: 12, border: `1px solid ${C.bdr}`,
      boxShadow: '0 8px 24px rgba(0,0,0,.12)', padding: 16,
    }}>
      <p style={{ fontSize: 12, fontWeight: 600, marginBottom: 4 }}>Delete contact?</p>
      <p style={{ fontSize: 11, color: C.t2, lineHeight: 1.5, marginBottom: 14 }}>
        <b>{name}</b> will be permanently removed.
      </p>
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
        <button onClick={onCancel} style={{ padding: '5px 14px', borderRadius: 8, border: `1px solid ${C.bdr}`, background: C.w, fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>Cancel</button>
        <button onClick={onConfirm} style={{ padding: '5px 14px', borderRadius: 8, border: 'none', background: C.red, color: '#fff', fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>Delete</button>
      </div>
    </div>
  );
}

/* -----------------------------------------------------------
   Inline viewport hook -- self-contained, no external deps
   ----------------------------------------------------------- */
function useViewport() {
  const [w, setW] = useState(typeof window !== 'undefined' ? window.innerWidth : 1200);
  useEffect(() => {
    const h = () => setW(window.innerWidth);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);
  return { mob: w < 768, tab: w >= 768 && w < 992 };
}

/* -----------------------------------------------------------
   Mobile contact card (super-responsive-table pattern)
   ----------------------------------------------------------- 
   Inspired by react-super-responsive-table: on mobile each
   <tr> becomes a card. Every <td> becomes a label:value row
   with the header text prepended (the "tdBefore" pattern).
   ALL columns are shown -- nothing is hidden.
   ----------------------------------------------------------- */
function ContactCard({ c, onEdit, onDelete, deleteConfirmId, setDeleteConfirmId }) {
  const stageCol = STAGE_COLORS[c.stage] || C.t3;

  /* label:value row styles -- mirrors the library's "pivoted" td */
  const kvRow = {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '9px 0', borderBottom: `1px solid ${C.bdr}`,
  };
  const kvLabel = {
    fontSize: 11, fontWeight: 700, color: C.t3,
    textTransform: 'uppercase', letterSpacing: '.06em',
    flexShrink: 0, width: '40%',
  };
  const kvValue = {
    fontSize: 13, color: C.t1, textAlign: 'right',
    overflow: 'hidden', textOverflow: 'ellipsis',
    whiteSpace: 'nowrap', maxWidth: '58%',
  };

  return (
    <div style={{
      background: C.w, borderRadius: 14,
      border: `1px solid ${C.bdr}`, overflow: 'hidden',
      boxShadow: '0 1px 4px rgba(0,0,0,.04)',
    }}>
      {/* ---- Card header: avatar + name + actions ---- */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 12,
        padding: '14px 16px', borderBottom: `1px solid ${C.bdr}`,
        background: `${stageCol}08`,
      }}>
        <div style={{
          width: 38, height: 38, borderRadius: '50%',
          background: `${stageCol}14`, display: 'grid',
          placeItems: 'center', flexShrink: 0,
        }}>
          <Icon name="person" size={20} style={{ color: stageCol }} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 700, fontSize: 14, lineHeight: 1.2 }}>{c.name}</div>
          {c.notes && <div style={{ fontSize: 10, color: C.t3 }}>Has notes</div>}
        </div>
        <div style={{ display: 'flex', gap: 4, flexShrink: 0, position: 'relative' }}>
          <button onClick={() => onEdit(c)} aria-label="Edit"
            style={{ width: 32, height: 32, borderRadius: 8, border: `1px solid ${C.bdr}`, background: C.w, cursor: 'pointer', display: 'grid', placeItems: 'center', color: C.t3 }}>
            <Icon name="edit" size={15} />
          </button>
          <button onClick={() => setDeleteConfirmId(deleteConfirmId === c.id ? null : c.id)} aria-label="Delete"
            style={{ width: 32, height: 32, borderRadius: 8, border: `1px solid ${C.bdr}`, background: C.w, cursor: 'pointer', display: 'grid', placeItems: 'center', color: C.t3 }}>
            <Icon name="delete" size={15} />
          </button>
          {deleteConfirmId === c.id && (
            <DeleteConfirm name={c.name} onConfirm={() => onDelete(c.id)} onCancel={() => setDeleteConfirmId(null)} />
          )}
        </div>
      </div>

      {/* ---- Card body: label:value rows (tdBefore pattern) ---- */}
      <div style={{ padding: '4px 16px 8px' }}>
        <div style={kvRow}>
          <span style={kvLabel}>Email</span>
          <span style={{ ...kvValue, color: c.email ? C.t2 : C.t3, fontStyle: c.email ? 'normal' : 'italic' }}>
            {c.email || '\u2014'}
          </span>
        </div>
        <div style={kvRow}>
          <span style={kvLabel}>Phone</span>
          <span style={{ ...kvValue, color: c.phone ? C.t2 : C.t3, fontStyle: c.phone ? 'normal' : 'italic' }}>
            {c.phone || '\u2014'}
          </span>
        </div>
        <div style={kvRow}>
          <span style={kvLabel}>Interest</span>
          <span style={kvValue}><Pill bg="#f1eeec" color={C.t2}>{c.interest}</Pill></span>
        </div>
        <div style={kvRow}>
          <span style={kvLabel}>Conversations</span>
          <span style={{ ...kvValue, fontWeight: 600 }}>{c.convos}</span>
        </div>
        <div style={kvRow}>
          <span style={kvLabel}>Last Active</span>
          <span style={{ ...kvValue, color: C.t2, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            {isActive(c.lastActive) && <span style={{ width: 6, height: 6, borderRadius: '50%', background: C.green, flexShrink: 0, boxShadow: `0 0 4px ${C.green}88` }} />}
            {c.lastActive}
          </span>
        </div>
        <div style={{ ...kvRow, borderBottom: 'none' }}>
          <span style={kvLabel}>Stage</span>
          <span style={kvValue}><Pill bg={`${stageCol}14`} color={stageCol}>{c.stage}</Pill></span>
        </div>
      </div>
    </div>
  );
}

/* -----------------------------------------------------------
   Main Component
   ----------------------------------------------------------- */
export default function ContactsPage() {
  const { mob, tab } = useViewport();

  /* Data */
  const [contacts, setContacts] = useState(SEED);
  const nextId = useRef(SEED.length + 1);

  /* Search / Filter / Sort */
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('All');
  const [page, setPage] = useState(1);
  const [sortCol, setSortCol] = useState(null);
  const [sortDir, setSortDir] = useState('asc');

  /* Modals */
  const [showAdd, setShowAdd] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);
  const blankForm = { name: '', email: '', phone: '', interest: INTERESTS[0], stage: 'New', notes: '' };
  const [form, setForm] = useState(blankForm);

  /* Import state */
  const [dragOver, setDragOver] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importRows, setImportRows] = useState([]);
  const [importMsg, setImportMsg] = useState(null);
  const fileInputRef = useRef(null);

  /* --- Filtering & Sorting --- */
  const filtered = useMemo(() => {
    let list = [...contacts];

    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(c =>
        c.name.toLowerCase().includes(q) ||
        c.email.toLowerCase().includes(q) ||
        c.interest.toLowerCase().includes(q) ||
        c.phone.includes(q) ||
        c.stage.toLowerCase().includes(q)
      );
    }

    if (filter === 'Has Email')        list = list.filter(c => c.email);
    else if (filter === 'Has Phone')   list = list.filter(c => c.phone);
    else if (filter === 'Active')      list = list.filter(c => isActive(c.lastActive));
    else if (filter === 'Inactive')    list = list.filter(c => !isActive(c.lastActive));
    else if (STAGES.includes(filter))  list = list.filter(c => c.stage === filter);

    if (sortCol) {
      list.sort((a, b) => {
        let av = a[sortCol], bv = b[sortCol];
        if (typeof av === 'number') return sortDir === 'asc' ? av - bv : bv - av;
        av = String(av).toLowerCase(); bv = String(bv).toLowerCase();
        return sortDir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
      });
    }
    return list;
  }, [contacts, search, filter, sortCol, sortDir]);

  /* --- Pagination --- */
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const rows = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);
  const rangeStart = filtered.length > 0 ? (safePage - 1) * PAGE_SIZE + 1 : 0;
  const rangeEnd = Math.min(safePage * PAGE_SIZE, filtered.length);

  const pageNums = useMemo(() => {
    const p = [];
    if (totalPages <= 7) { for (let i = 1; i <= totalPages; i++) p.push(i); }
    else {
      p.push(1);
      if (safePage > 3) p.push('\u2026');
      for (let i = Math.max(2, safePage - 1); i <= Math.min(totalPages - 1, safePage + 1); i++) p.push(i);
      if (safePage < totalPages - 2) p.push('\u2026');
      p.push(totalPages);
    }
    return p;
  }, [totalPages, safePage]);

  const doSearch = (v) => { setSearch(v); setPage(1); };
  const doFilter = (f) => { setFilter(f); setPage(1); };
  const doSort = (col) => {
    if (sortCol === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortCol(col); setSortDir('asc'); }
    setPage(1);
  };

  /* --- CRUD --- */
  const openAdd = () => { setEditingId(null); setForm(blankForm); setShowAdd(true); };
  const openEdit = (c) => {
    setEditingId(c.id);
    setForm({ name: c.name, email: c.email, phone: c.phone, interest: c.interest, stage: c.stage, notes: c.notes || '' });
    setShowAdd(true);
  };
  const closeAdd = () => { setShowAdd(false); setEditingId(null); };

  const saveContact = () => {
    if (!form.name.trim()) return;
    if (editingId) {
      setContacts(prev => prev.map(c =>
        c.id === editingId
          ? { ...c, name: form.name.trim(), email: form.email.trim(), phone: form.phone.trim(), interest: form.interest, stage: form.stage, notes: form.notes.trim() }
          : c
      ));
    } else {
      setContacts(prev => [...prev, {
        id: nextId.current++,
        name: form.name.trim(), email: form.email.trim(), phone: form.phone.trim(),
        interest: form.interest, stage: form.stage, notes: form.notes.trim(),
        convos: 0, lastActive: 'Just now',
      }]);
    }
    closeAdd();
  };

  const deleteContact = (id) => { setContacts(prev => prev.filter(c => c.id !== id)); setDeleteConfirmId(null); };

  /* --- CSV import --- */
  const parseCSV = useCallback((text) => {
    const lines = text.trim().split('\n').filter(Boolean);
    if (lines.length < 2) return [];
    const hdr = lines[0].split(',').map(h => h.trim().toLowerCase().replace(/['"]/g, ''));
    return lines.slice(1).map(line => {
      const vals = line.split(',').map(v => v.trim().replace(/['"]/g, ''));
      const o = {};
      hdr.forEach((h, i) => { o[h] = vals[i] || ''; });
      return {
        name: o.name || o.full_name || o.fullname || '',
        email: o.email || '',
        phone: o.phone || o.telephone || '',
        interest: INTERESTS.includes(o.interest) ? o.interest : (o.interest || o.interests || INTERESTS[0]),
        stage: STAGES.includes(o.stage) ? o.stage : 'New',
      };
    }).filter(r => r.name);
  }, []);

  const handleFile = useCallback((file) => {
    if (!file) return;
    const ext = file.name.split('.').pop().toLowerCase();
    if (ext !== 'csv' && file.type !== 'text/csv') {
      setImportMsg({ type: 'error', text: 'Only .csv files are supported.' });
      return;
    }
    setImportFile(file);
    setImportMsg(null);
    const reader = new FileReader();
    reader.onload = (e) => {
      const parsed = parseCSV(e.target.result);
      if (parsed.length === 0) { setImportMsg({ type: 'error', text: 'No valid rows found. Check your CSV headers: name, email, phone, interest, stage.' }); return; }
      setImportRows(parsed);
    };
    reader.readAsText(file);
  }, [parseCSV]);

  const doImport = () => {
    if (!importFile || importRows.length === 0) return;
    const newContacts = importRows.map(r => ({ ...r, id: nextId.current++, convos: 0, lastActive: 'Just now', notes: '' }));
    setContacts(prev => [...prev, ...newContacts]);
    setImportMsg({ type: 'success', text: `Successfully imported ${newContacts.length} contact${newContacts.length !== 1 ? 's' : ''}!` });
    setTimeout(() => closeImport(), 1400);
  };

  const closeImport = () => { setShowImport(false); setImportFile(null); setImportRows([]); setImportMsg(null); };

  const onDrop = useCallback((e) => { e.preventDefault(); setDragOver(false); handleFile(e.dataTransfer?.files?.[0]); }, [handleFile]);

  /* --- Style helpers --- */
  const inp = { width: '100%', padding: '10px 14px', borderRadius: 10, border: `1px solid ${C.bdr}`, fontSize: 13, outline: 'none', fontFamily: 'inherit', transition: 'border .15s, box-shadow .15s' };
  const lbl = { display: 'block', fontSize: 11, fontWeight: 600, color: C.t2, marginBottom: 6, textTransform: 'uppercase', letterSpacing: '.04em' };
  const foc = {
    onFocus: e => { e.target.style.borderColor = C.primary; e.target.style.boxShadow = `0 0 0 3px ${C.pL}`; },
    onBlur:  e => { e.target.style.borderColor = C.bdr;     e.target.style.boxShadow = 'none'; },
  };

  /* --- Sortable header cell (desktop only) --- */
  const Th = ({ col, label, sx }) => (
    <th onClick={() => doSort(col)} style={{ padding: '12px 20px', textAlign: 'left', fontSize: 10, fontWeight: 700, color: C.t3, textTransform: 'uppercase', letterSpacing: '.08em', cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap', ...sx }}>
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}>
        {label}
        {sortCol === col
          ? <Icon name={sortDir === 'asc' ? 'arrow_upward' : 'arrow_downward'} size={12} style={{ color: C.primary }} />
          : <Icon name="unfold_more" size={12} style={{ color: '#d6d3d1' }} />}
      </span>
    </th>
  );

  /* --- Responsive modal helpers --- */
  const modalW = (base) => ({ width: mob ? '100%' : base, maxWidth: mob ? 'none' : base });
  const mPadH = mob ? '16px 16px' : '22px 28px';
  const mPadB = mob ? '20px 16px' : '24px 28px';
  const mPadF = mob ? '14px 16px' : '16px 28px';

  /* --- Pagination button renderer (shared between mobile/desktop) --- */
  const PaginationBtn = ({ size = 32 }) => (
    <div style={{ display: 'flex', gap: 4, alignItems: 'center', flexWrap: 'wrap', justifyContent: 'center' }}>
      <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={safePage <= 1}
        style={{ width: size, height: size, borderRadius: 8, border: `1px solid ${C.bdr}`, background: C.w, display: 'grid', placeItems: 'center', cursor: safePage <= 1 ? 'default' : 'pointer', color: safePage <= 1 ? '#ddd' : C.t2 }}>
        <Icon name="chevron_left" size={16} />
      </button>
      {pageNums.map((n, i) => (
        <button key={`${n}-${i}`}
          onClick={() => typeof n === 'number' && setPage(n)}
          disabled={typeof n !== 'number'}
          style={{
            width: size, height: size, borderRadius: 8, fontSize: 12, fontWeight: 600, display: 'grid', placeItems: 'center',
            border: n === safePage ? 'none' : `1px solid ${C.bdr}`,
            background: n === safePage ? C.primary : C.w,
            color: n === safePage ? '#fff' : typeof n !== 'number' ? C.t3 : C.t2,
            cursor: typeof n !== 'number' ? 'default' : 'pointer',
          }}>
          {n}
        </button>
      ))}
      <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={safePage >= totalPages}
        style={{ width: size, height: size, borderRadius: 8, border: `1px solid ${C.bdr}`, background: C.w, display: 'grid', placeItems: 'center', cursor: safePage >= totalPages ? 'default' : 'pointer', color: safePage >= totalPages ? '#ddd' : C.t2 }}>
        <Icon name="chevron_right" size={16} />
      </button>
    </div>
  );

  /* -----------------------------------------------------------
     RENDER
     ----------------------------------------------------------- */
  return (
  <>
    <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, overflowY: 'auto' }}>
      <div style={{ padding: mob ? '20px 16px 32px' : '32px 36px 48px' }}>
        <Breadcrumb items={['Console', 'Contacts']} />

        {/* --- Header --- */}
        <div style={{ display: 'flex', flexDirection: mob ? 'column' : 'row', justifyContent: 'space-between', alignItems: mob ? 'stretch' : 'center', gap: mob ? 14 : 0, marginBottom: 24 }}>
          <h2 style={{ fontSize: mob ? 24 : 32, fontWeight: 900, letterSpacing: '-.03em' }}>Contacts</h2>
          <div style={{ display: 'flex', gap: 10 }}>
            <Btn icon="file_upload" onClick={() => { setShowImport(true); setImportFile(null); setImportRows([]); setImportMsg(null); }}>Import</Btn>
            <Btn primary icon="person_add" onClick={openAdd}>Add Contact</Btn>
          </div>
        </div>

        {/* --- Search --- */}
        <div style={{ position: 'relative', marginBottom: 16 }}>
          <Icon name="search" size={20} style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: C.t3, pointerEvents: 'none' }} />
          <input value={search} onChange={e => doSearch(e.target.value)}
            placeholder="Search contacts by name, email, or interest..."
            style={{ width: '100%', padding: '12px 16px 12px 44px', borderRadius: 14, border: `1px solid ${C.bdr}`, background: C.w, fontSize: 13, outline: 'none', transition: 'border .15s' }}
            onFocus={e => e.target.style.borderColor = C.primary}
            onBlur={e => e.target.style.borderColor = C.bdr} />
          {search && (
            <button onClick={() => doSearch('')} style={{ position: 'absolute', right: 14, top: '50%', transform: 'translateY(-50%)', border: 'none', background: 'none', cursor: 'pointer', color: C.t3, display: 'grid', placeItems: 'center' }}>
              <Icon name="close" size={18} />
            </button>
          )}
        </div>

        {/* --- Filters (horizontal scroll on mobile) --- */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 24, flexWrap: mob ? 'nowrap' : 'wrap', overflowX: mob ? 'auto' : 'visible', WebkitOverflowScrolling: 'touch', paddingBottom: mob ? 4 : 0 }}>
          {['All', 'Has Email', 'Has Phone', 'Active', 'Inactive', ...STAGES].map(f => (
            <button key={f} onClick={() => doFilter(f)} style={{
              padding: '6px 16px', borderRadius: 99, fontSize: 12, fontWeight: 600, cursor: 'pointer', transition: 'all .15s', flexShrink: 0,
              border: filter === f ? 'none' : `1px solid ${C.bdr}`,
              background: filter === f ? (STAGE_COLORS[f] || C.primary) : C.w,
              color: filter === f ? '#fff' : C.t2,
            }}>{f}</button>
          ))}
        </div>

        {/* --- Results count on search --- */}
        {search && (
          <p style={{ fontSize: 12, color: C.t2, marginBottom: 12 }}>
            Found <b>{filtered.length}</b> contact{filtered.length !== 1 ? 's' : ''} matching &quot;<b>{search}</b>&quot;
          </p>
        )}

        {/* =============================================================
            MOBILE: Card list (super-responsive-table pattern)
            Each table row becomes a card with label:value pairs.
            ALL columns visible -- nothing hidden.
            ============================================================= */}
        {mob ? (
          <div>
            {rows.length === 0 ? (
              <Card style={{ padding: 48, textAlign: 'center' }}>
                <Icon name="person_search" size={36} style={{ color: C.t3, display: 'block', margin: '0 auto 10px' }} />
                <div style={{ fontSize: 15, fontWeight: 700, color: C.t1, marginBottom: 4 }}>No contacts found</div>
                <div style={{ fontSize: 12, color: C.t3 }}>Try adjusting your search or filter criteria</div>
              </Card>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {rows.map(c => (
                  <ContactCard key={c.id} c={c} onEdit={openEdit} onDelete={deleteContact}
                    deleteConfirmId={deleteConfirmId} setDeleteConfirmId={setDeleteConfirmId} />
                ))}
              </div>
            )}
            {/* Mobile pagination */}
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10, marginTop: 20, fontSize: 12, color: C.t2 }}>
              <span>Showing <b>{rangeStart}</b>&ndash;<b>{rangeEnd}</b> of <b>{filtered.length}</b></span>
              <PaginationBtn size={36} />
            </div>
          </div>
        ) : (

        /* =============================================================
           DESKTOP / TABLET: Original table (unchanged)
           ============================================================= */
        <Card style={{ overflow: 'hidden' }}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, minWidth: 960 }}>
              <thead>
                <tr style={{ background: C.bg, borderBottom: `1px solid ${C.bdr}` }}>
                  <Th col="name"       label="Name" />
                  <Th col="email"      label="Email" />
                  <Th col="phone"      label="Phone" />
                  <Th col="interest"   label="Interests" />
                  <Th col="convos"     label="Conversations" sx={{ textAlign: 'center' }} />
                  <Th col="lastActive" label="Last Active" />
                  <Th col="stage"      label="Stage" />
                  <th style={{ padding: '12px 16px', width: 56 }} />
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr>
                    <td colSpan={8} style={{ padding: 56, textAlign: 'center' }}>
                      <Icon name="person_search" size={36} style={{ color: C.t3, display: 'block', margin: '0 auto 10px' }} />
                      <div style={{ fontSize: 15, fontWeight: 700, color: C.t1, marginBottom: 4 }}>No contacts found</div>
                      <div style={{ fontSize: 12, color: C.t3 }}>Try adjusting your search or filter criteria</div>
                    </td>
                  </tr>
                ) : rows.map(c => (
                  <tr key={c.id} className="hover-row" style={{ borderBottom: `1px solid ${C.bdr}` }}>
                    <td style={{ padding: '14px 20px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <div style={{ width: 34, height: 34, borderRadius: '50%', background: `${STAGE_COLORS[c.stage] || C.t3}12`, display: 'grid', placeItems: 'center', flexShrink: 0 }}>
                          <Icon name="person" size={18} style={{ color: STAGE_COLORS[c.stage] || C.primary }} />
                        </div>
                        <div>
                          <span style={{ fontWeight: 600, display: 'block', lineHeight: 1.2 }}>{c.name}</span>
                          {c.notes && <span style={{ fontSize: 10, color: C.t3, lineHeight: 1 }}>Has notes</span>}
                        </div>
                      </div>
                    </td>
                    <td style={{ padding: '14px 20px', color: c.email ? C.t2 : C.t3, fontStyle: c.email ? 'normal' : 'italic' }}>{c.email || '\u2014'}</td>
                    <td style={{ padding: '14px 20px', color: c.phone ? C.t2 : C.t3, fontStyle: c.phone ? 'normal' : 'italic' }}>{c.phone || '\u2014'}</td>
                    <td style={{ padding: '14px 20px' }}><Pill bg="#f1eeec" color={C.t2}>{c.interest}</Pill></td>
                    <td style={{ padding: '14px 20px', textAlign: 'center', fontWeight: 600 }}>{c.convos}</td>
                    <td style={{ padding: '14px 20px', color: C.t2 }}>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                        {isActive(c.lastActive) && <span style={{ width: 6, height: 6, borderRadius: '50%', background: C.green, display: 'inline-block', boxShadow: `0 0 4px ${C.green}88` }} />}
                        {c.lastActive}
                      </span>
                    </td>
                    <td style={{ padding: '14px 20px' }}>
                      <Pill bg={`${STAGE_COLORS[c.stage] || C.t3}14`} color={STAGE_COLORS[c.stage] || C.t3}>{c.stage}</Pill>
                    </td>
                    <td style={{ padding: '14px 12px', position: 'relative' }}>
                      <div style={{ display: 'flex', gap: 2 }}>
                        <button onClick={() => openEdit(c)} title="Edit"
                          style={{ width: 28, height: 28, borderRadius: 6, border: 'none', background: 'transparent', cursor: 'pointer', display: 'grid', placeItems: 'center', color: C.t3, transition: 'color .15s' }}
                          onMouseEnter={e => e.currentTarget.style.color = C.primary}
                          onMouseLeave={e => e.currentTarget.style.color = C.t3}>
                          <Icon name="edit" size={15} />
                        </button>
                        <button onClick={() => setDeleteConfirmId(deleteConfirmId === c.id ? null : c.id)} title="Delete"
                          style={{ width: 28, height: 28, borderRadius: 6, border: 'none', background: 'transparent', cursor: 'pointer', display: 'grid', placeItems: 'center', color: C.t3, transition: 'color .15s' }}
                          onMouseEnter={e => e.currentTarget.style.color = C.red}
                          onMouseLeave={e => e.currentTarget.style.color = C.t3}>
                          <Icon name="delete" size={15} />
                        </button>
                      </div>
                      {deleteConfirmId === c.id && (
                        <DeleteConfirm name={c.name} onConfirm={() => deleteContact(c.id)} onCancel={() => setDeleteConfirmId(null)} />
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* --- Desktop Pagination --- */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 20px', borderTop: `1px solid ${C.bdr}`, fontSize: 12, color: C.t2 }}>
            <span>Showing <b>{rangeStart}</b> to <b>{rangeEnd}</b> of <b>{filtered.length}</b> results</span>
            <PaginationBtn />
          </div>
        </Card>
        )}
      </div>
    </div>

    {/* -------------------------------------------------------
        ADD / EDIT CONTACT MODAL
       ------------------------------------------------------- */}
    {showAdd && (
      <Backdrop onClose={closeAdd}>
        <div style={{ ...modalW(520), maxHeight: '92vh', background: C.w, borderRadius: mob ? 14 : 18, boxShadow: '0 24px 64px rgba(0,0,0,.14)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          {/* Header */}
          <div style={{ padding: mPadH, borderBottom: `1px solid ${C.bdr}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{ width: 36, height: 36, borderRadius: 10, background: C.pL, display: 'grid', placeItems: 'center' }}>
                <Icon name={editingId ? 'edit' : 'person_add'} size={20} style={{ color: C.primary }} />
              </div>
              <div>
                <h3 style={{ fontSize: 17, fontWeight: 700, lineHeight: 1.15 }}>{editingId ? 'Edit Contact' : 'Add New Contact'}</h3>
                <p style={{ fontSize: 11, color: C.t3, marginTop: 1 }}>{editingId ? 'Update contact information' : 'Fill in the contact details below'}</p>
              </div>
            </div>
            <button onClick={closeAdd} style={{ border: 'none', background: '#f1eeec', borderRadius: 8, width: 30, height: 30, cursor: 'pointer', display: 'grid', placeItems: 'center', color: C.t2 }}>
              <Icon name="close" size={18} />
            </button>
          </div>

          {/* Body */}
          <div style={{ padding: mPadB, display: 'flex', flexDirection: 'column', gap: 18, overflowY: 'auto', flex: 1 }}>
            <div>
              <label style={lbl}>Full Name <span style={{ color: C.red }}>*</span></label>
              <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="e.g. Aditya Wijaya" autoFocus style={inp} {...foc} />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: mob ? '1fr' : '1fr 1fr', gap: 14 }}>
              <div>
                <label style={lbl}>Email</label>
                <input value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} type="email" placeholder="email@example.com" style={inp} {...foc} />
              </div>
              <div>
                <label style={lbl}>Phone</label>
                <input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} placeholder="+62 812-3456-7890" style={inp} {...foc} />
              </div>
            </div>

            <div>
              <label style={lbl}>Interest</label>
              <select value={form.interest} onChange={e => setForm({ ...form, interest: e.target.value })} style={{ ...inp, cursor: 'pointer', appearance: 'auto' }}>
                {INTERESTS.map(v => <option key={v} value={v}>{v}</option>)}
              </select>
            </div>

            <div>
              <label style={lbl}>Stage</label>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {STAGES.map(s => {
                  const active = form.stage === s;
                  const col = STAGE_COLORS[s];
                  return (
                    <button key={s} onClick={() => setForm({ ...form, stage: s })} style={{
                      padding: '7px 16px', borderRadius: 10, fontSize: 12, fontWeight: 600, cursor: 'pointer', transition: 'all .15s',
                      border: active ? `2px solid ${col}` : `1px solid ${C.bdr}`,
                      background: active ? `${col}10` : C.w,
                      color: active ? col : C.t2,
                    }}>
                      {active && <Icon name="check" size={13} style={{ marginRight: 4, verticalAlign: -2 }} />}
                      {s}
                    </button>
                  );
                })}
              </div>
            </div>

            <div>
              <label style={lbl}>Notes</label>
              <textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} rows={3}
                placeholder="Optional notes about this contact..."
                style={{ ...inp, resize: 'vertical', lineHeight: 1.5 }} {...foc} />
            </div>
          </div>

          {/* Footer */}
          <div style={{ padding: mPadF, borderTop: `1px solid ${C.bdr}`, display: 'flex', flexDirection: mob ? 'column-reverse' : 'row', justifyContent: 'space-between', alignItems: mob ? 'stretch' : 'center', gap: mob ? 10 : 0, flexShrink: 0 }}>
            <span style={{ fontSize: 11, color: C.t3, textAlign: mob ? 'center' : 'left' }}><span style={{ color: C.red }}>*</span> Required field</span>
            <div style={{ display: 'flex', gap: 10, justifyContent: mob ? 'center' : 'flex-end' }}>
              <Btn onClick={closeAdd}>Cancel</Btn>
              <Btn primary icon={editingId ? 'check' : 'person_add'} onClick={saveContact}
                style={{ opacity: form.name.trim() ? 1 : 0.5, pointerEvents: form.name.trim() ? 'auto' : 'none' }}>
                {editingId ? 'Save Changes' : 'Add Contact'}
              </Btn>
            </div>
          </div>
        </div>
      </Backdrop>
    )}

    {/* -------------------------------------------------------
        IMPORT MODAL
       ------------------------------------------------------- */}
    {showImport && (
      <Backdrop onClose={closeImport}>
        <div style={{ ...modalW(560), maxHeight: '92vh', background: C.w, borderRadius: mob ? 14 : 18, boxShadow: '0 24px 64px rgba(0,0,0,.14)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          {/* Header */}
          <div style={{ padding: mPadH, borderBottom: `1px solid ${C.bdr}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{ width: 36, height: 36, borderRadius: 10, background: C.pL, display: 'grid', placeItems: 'center' }}>
                <Icon name="upload_file" size={20} style={{ color: C.primary }} />
              </div>
              <div>
                <h3 style={{ fontSize: 17, fontWeight: 700, lineHeight: 1.15 }}>Import Contacts</h3>
                <p style={{ fontSize: 11, color: C.t3, marginTop: 1 }}>Upload a CSV file to bulk-add contacts</p>
              </div>
            </div>
            <button onClick={closeImport} style={{ border: 'none', background: '#f1eeec', borderRadius: 8, width: 30, height: 30, cursor: 'pointer', display: 'grid', placeItems: 'center', color: C.t2 }}>
              <Icon name="close" size={18} />
            </button>
          </div>

          {/* Body */}
          <div style={{ padding: mPadB, overflowY: 'auto', flex: 1 }}>
            {/* Drop zone */}
            <div
              onDragOver={e => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={onDrop}
              onClick={() => fileInputRef.current?.click()}
              style={{
                border: `2px dashed ${dragOver ? C.primary : C.bdr}`,
                borderRadius: 16, padding: '36px 20px', textAlign: 'center', cursor: 'pointer',
                background: dragOver ? C.pL : '#faf9f8', transition: 'all .2s',
              }}
            >
              <input ref={fileInputRef} type="file" accept=".csv" style={{ display: 'none' }}
                onChange={e => { handleFile(e.target.files?.[0]); e.target.value = ''; }} />

              <div style={{ width: 56, height: 56, borderRadius: 16, background: importFile ? `${C.green}12` : C.pL, display: 'grid', placeItems: 'center', margin: '0 auto 14px', transition: 'background .2s' }}>
                <Icon name={importFile ? 'description' : 'cloud_upload'} size={28} style={{ color: importFile ? C.green : C.primary }} />
              </div>

              {importFile ? (
                <>
                  <div style={{ fontSize: 14, fontWeight: 700, color: C.t1, marginBottom: 3 }}>{importFile.name}</div>
                  <div style={{ fontSize: 12, color: C.t3 }}>{(importFile.size / 1024).toFixed(1)} KB &bull; {importRows.length} rows detected</div>
                  <button onClick={e => { e.stopPropagation(); setImportFile(null); setImportRows([]); setImportMsg(null); }}
                    style={{ marginTop: 10, border: 'none', background: 'none', color: C.primary, fontSize: 12, fontWeight: 600, cursor: 'pointer', textDecoration: 'underline' }}>
                    Choose a different file
                  </button>
                </>
              ) : (
                <>
                  <div style={{ fontSize: 14, fontWeight: 700, color: C.t1, marginBottom: 4 }}>Drop CSV file here or click to browse</div>
                  <div style={{ fontSize: 12, color: C.t3 }}>Supports <b>.csv</b> files up to 5 MB</div>
                </>
              )}
            </div>

            {/* Status messages */}
            {importMsg && (
              <div style={{
                marginTop: 16, display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px', borderRadius: 12,
                background: importMsg.type === 'success' ? '#dcfce7' : `${C.red}0c`,
              }}>
                <Icon name={importMsg.type === 'success' ? 'check_circle' : 'error'} size={20} style={{ color: importMsg.type === 'success' ? C.green : C.red, flexShrink: 0 }} />
                <span style={{ fontSize: 13, fontWeight: 600, color: importMsg.type === 'success' ? C.green : C.red }}>{importMsg.text}</span>
              </div>
            )}

            {/* Preview table */}
            {importRows.length > 0 && !importMsg && (
              <div style={{ marginTop: 20 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: C.t3, textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 10 }}>
                  Preview &mdash; first {Math.min(5, importRows.length)} of {importRows.length} rows
                </div>
                <div style={{ border: `1px solid ${C.bdr}`, borderRadius: 12, overflow: 'hidden', overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, minWidth: mob ? 420 : 'auto' }}>
                    <thead>
                      <tr style={{ background: C.bg }}>
                        {['Name', 'Email', 'Phone', 'Interest', 'Stage'].map(h => (
                          <th key={h} style={{ padding: '8px 12px', textAlign: 'left', fontSize: 10, fontWeight: 700, color: C.t3, textTransform: 'uppercase', letterSpacing: '.04em' }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {importRows.slice(0, 5).map((r, i) => (
                        <tr key={i} style={{ borderTop: `1px solid ${C.bdr}` }}>
                          <td style={{ padding: '8px 12px', fontWeight: 600 }}>{r.name}</td>
                          <td style={{ padding: '8px 12px', color: r.email ? C.t2 : C.t3 }}>{r.email || '\u2014'}</td>
                          <td style={{ padding: '8px 12px', color: r.phone ? C.t2 : C.t3 }}>{r.phone || '\u2014'}</td>
                          <td style={{ padding: '8px 12px' }}>{r.interest}</td>
                          <td style={{ padding: '8px 12px' }}>
                            <Pill bg={`${STAGE_COLORS[r.stage] || C.t3}14`} color={STAGE_COLORS[r.stage] || C.t3}>{r.stage}</Pill>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Template hint */}
            <div style={{ marginTop: 20, padding: '14px 18px', borderRadius: 12, background: '#f1eeec', display: 'flex', gap: 12, alignItems: 'flex-start' }}>
              <Icon name="info" size={18} style={{ color: C.t3, flexShrink: 0, marginTop: 1 }} />
              <div style={{ fontSize: 12, color: C.t2, lineHeight: 1.6 }}>
                <b>CSV format:</b> Your file should include headers &mdash;
                <span style={{ fontFamily: 'monospace', fontSize: 11, background: C.w, padding: '2px 6px', borderRadius: 4, margin: '0 3px' }}>name, email, phone, interest, stage</span>.
                Valid stages: {STAGES.join(', ')}.
              </div>
            </div>
          </div>

          {/* Footer */}
          <div style={{ padding: mPadF, borderTop: `1px solid ${C.bdr}`, display: 'flex', flexDirection: mob ? 'column' : 'row', justifyContent: 'space-between', alignItems: mob ? 'stretch' : 'center', gap: mob ? 10 : 0, flexShrink: 0 }}>
            <span style={{ fontSize: 11, color: C.t3, textAlign: mob ? 'center' : 'left' }}>
              {importRows.length > 0 && !importMsg && `${importRows.length} contact${importRows.length !== 1 ? 's' : ''} ready to import`}
            </span>
            <div style={{ display: 'flex', gap: 10, justifyContent: mob ? 'center' : 'flex-end' }}>
              <Btn onClick={closeImport}>Cancel</Btn>
              <Btn primary icon="file_upload" onClick={doImport}
                style={{ opacity: importRows.length > 0 && !importMsg ? 1 : 0.5, pointerEvents: importRows.length > 0 && !importMsg ? 'auto' : 'none' }}>
                Import {importRows.length > 0 ? `${importRows.length} Contacts` : ''}
              </Btn>
            </div>
          </div>
        </div>
      </Backdrop>
    )}
  </>
  );
}
